import { useEffect, useRef } from "react";

// Keyboard and screen-reader basics for a modal (audit client-09): the
// picture viewers (components/Lightbox.jsx) and TermsGate.
// - On open the focus moves to the dialog itself (ref: its element, with
//   tabIndex={-1}), so a screen reader announces it.
// - Tab and Shift+Tab stay inside it while `trap` is true (trapTab) — the
//   page behind can't be reached from the keyboard.
// - Escape calls onEscape, when there is one (TermsGate has none: it is
//   not dismissible).
// - On close the focus goes back to whatever opened it.
// trap / onEscape are read when a key is pressed: callers may pass new ones
// on every render.
const FOCUSABLE = [
  "a[href]", "button:not([disabled])", "input:not([disabled])", "select:not([disabled])",
  "textarea:not([disabled])", "[tabindex]:not([tabindex='-1'])",
].join(",");

export function focusables(el) {
  if (!el) return [];
  return [...el.querySelectorAll(FOCUSABLE)].filter((n) => !n.closest("[inert]") && n.getAttribute("aria-hidden") !== "true");
}

// Tab at the last control goes to the first, Shift+Tab at the first (or on
// the dialog itself) to the last; focus that is outside comes back in.
export function trapTab(e, el) {
  if (!el || e.key !== "Tab") return;
  const list = focusables(el);
  const active = document.activeElement;
  if (!list.length) {
    e.preventDefault();
    el.focus();
    return;
  }
  const first = list[0];
  const last = list[list.length - 1];
  if (!el.contains(active)) {
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  } else if (e.shiftKey && (active === first || active === el)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

export function useModalFocus(ref, { onEscape = null, trap = true } = {}) {
  const latest = useRef({ onEscape, trap });
  latest.current = { onEscape, trap };
  useEffect(() => {
    const opener = document.activeElement;
    ref.current?.focus?.();
    const onKey = (e) => {
      const { onEscape: close, trap: trapping } = latest.current;
      if (e.key === "Escape" && close) {
        e.stopPropagation();
        close();
      } else if (e.key === "Tab" && trapping) {
        trapTab(e, ref.current);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener && opener !== document.body && typeof opener.focus === "function" && document.contains(opener)) {
        opener.focus();
      }
    };
  }, [ref]);
}
